<template>
  <v-container>
    <v-row justify="space-between" align="center" class="mb-2">
      <v-col cols="auto">
        <span class="font-weight-light" style="font-size: 16pt">Invoices</span>
      </v-col>
      <v-col cols="auto">
        <v-btn color="primary" @click="openUploadDialog" depressed>
          {{ $t('label.uploadInvoice') }}
          <v-icon right>mdi-cloud-upload</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-form v-model="isFormSearchValid">
            <v-card-text class="pb-0">
              <v-row align="center" dense>
                <v-col cols="12" sm="3">
                  <v-text-field type="date" :label="$t('InvoicePage.DateFrom')" v-model="filters.dateFrom" dense outlined
                    clearable :rules="[rules.required]" hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field :label="$t('InvoicePage.SupplierReference')" v-model="filters.invoice" dense outlined
                    clearable hide-details>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-combobox :label="$t('InvoicePage.InvoiceStatus')" v-model="filters.invoiceStatus"
                    :items="invoiceStatusList" item-value="value" item-text="name" dense outlined clearable hide-details>
                  </v-combobox>
                </v-col>
                <v-col cols="auto">
                  <v-btn color="primary" @click="getInvoices" :disabled="!isFormSearchValid" depressed>
                    {{ $t('InvoicePage.Search') }}
                    <v-icon right>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row align="center" dense class="mt-2">
                <v-col cols="12" sm="3">
                  <v-select :disabled="!isFormSearchValid" v-model="filters.isComplete" :items="threeWayList"
                    item-text="text" item-value="value" :label="$t('InvoicePage.3Way')"
                    @change="getInvoices" dense outlined clearable hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select :disabled="!isFormSearchValid" v-model="filters.passDue" :items="passDueList"
                    item-text="text" item-value="value" :label="$t('InvoicePage.PassDue')"
                    @change="getInvoices" dense outlined clearable hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select :disabled="!isFormSearchValid" v-model="filters.onHold" :items="onHoldList"
                    item-text="text" item-value="value" :label="$t('InvoicePage.OnHold')"
                    @change="getInvoices" dense outlined clearable hide-details></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>
          <v-card-text>
            <v-data-table :items="invoices" :loading="loading.invoices" :headers="headers" show-expand single-expand
              :options.sync="options" :expanded.sync="expanded" @item-expanded="onExpanded"
              :server-items-length="totalOfItems" :footer-props="footerProps" item-key="shipmentInvoiceId">
              <template v-slot:[`item.invoiceStatus`]="{ item }">
                <v-chip label :color="getInvoiceStatusColor(item.invoiceStatus)"
                  :text-color="getInvoiceStatusTextColor1(item.invoiceStatus)">
                  {{ $t('InvoicePage.' + getInvoiceStatusName(item.invoiceStatus)) }}
                  <v-icon size="16" v-if="item.holdReason" :title="$t('InvoicePage.Reason')" right
                    @click.stop="openHoldReasonDialog(item)">
                    mdi-comment-text
                  </v-icon>
                  <v-icon size="16" v-if="item.rejectionReason" :title="$t('InvoicePage.Reason')" right
                    @click.stop="openReasonDialog(item)">
                    mdi-comment-text
                  </v-icon>
                </v-chip>
              </template>
              <template v-slot:[`item.dueDateFormatted`]="{ item }">
                <v-chip label :color="getPassDueColor(!item.isPassDue)" text-color="#FFF">
                  {{ item.dueDateFormatted }}
                </v-chip>
              </template>
              <template v-slot:[`item.shipmentStatus`]="{ item }">
                <v-container>
                  <div>
                    <v-chip style="width:170px;" label :color="getStatusColor(item.isQuantityComplete)"
                      :text-color="getStatusTextColor(item.isQuantityComplete)">
                      {{ $t('InvoicePage.' + getQuantityStatusName(item.isQuantityComplete)) }}
                    </v-chip>
                  </div>
                  <div style="margin-top: 3px; margin-bottom: 3px;">
                    <v-chip style="width:170px;" label :color="getStatusColor(item.isTotalComplete)"
                      :text-color="getStatusTextColor(item.isTotalComplete)">
                      {{ $t('InvoicePage.' + getTotalStatusName(item.isTotalComplete)) }}
                    </v-chip>
                  </div>
                  <div>
                    <v-chip style="width:170px;" label :color="getShipmentStatusColor(item.shipmentStatus)"
                      :text-color="getInvoiceStatusTextColor(item.shipmentStatus)">
                      {{ $t('InvoicePage.' + getShipmentStatusName(item.shipmentStatus)) }}
                    </v-chip>
                  </div>
                </v-container>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <v-tabs>
                    <v-tab>
                      {{ $t('InvoicePage.Summary') }}
                    </v-tab>
                    <v-tab>
                      {{ $t('InvoicePage.Shipments') }}
                    </v-tab>
                    <v-tab-item>
                      <v-data-table :headers="Summary" :loading="loading.billedShipments"
                        :items="JSON.parse('[' + JSON.stringify(item) + ']')" hide-default-footer disable-pagination>
                        <template v-slot:[`item.quantity1`]="{ item }">
                          {{ item.quantity1 }}
                        </template>
                        <template v-slot:[`item.poAmountFormatted`]="{ item }">
                          <v-chip label :color="getPassDueColor(item.isTotalComplete === 1)" text-color="#FFF">
                            {{ item.poAmountFormatted }}
                            <!-- {{ item.total1 }} -->
                          </v-chip>
                        </template>
                        <template v-slot:[`item.quantity2`]="{ item }">
                          <v-chip label :color="getPassDueColor(item.isQuantityComplete === 1)" text-color="#FFF">
                            {{ parseFloat(item.quantity2).toFixed(3) }}
                          </v-chip>
                        </template>
                        <template v-slot:[`item.invoiceAmountFormatted`]="{ item }">
                          <v-chip label :color="getPassDueColor(item.isTotalComplete === 1)" text-color="#FFF">
                            {{ item.invoiceAmountFormatted }}
                            <!-- {{ item.total2 }} -->
                          </v-chip>
                        </template>
                        <template v-slot:[`item.confirmedQuantity`]="{ item }">
                          <v-chip label :color="getPassDueColor(item.isQuantityComplete === 1)" text-color="#FFF">
                            {{ parseFloat(item.confirmedQuantity).toFixed(3) }}
                          </v-chip>
                        </template>
                        <template v-slot:[`item.quantityToShip`]="{ item }">
                          {{ parseFloat(item.quantityToShip).toFixed(3) }}
                        </template>
                      </v-data-table>
                    </v-tab-item>
                    <v-tab-item>
                      <v-data-table :headers="headersExpand" :loading="loading.billedShipments"
                        :items="item.billedShipments" hide-default-footer disable-pagination>
                        <template v-slot:[`item.confirmationStatus`]="{ item }">
                          <v-chip label :color="getConfirmationStatusColor(item.confirmationStatus)"
                            :text-color="getConfirmationStatusTextColor(item.confirmationStatus)">
                            {{ $t('InvoicePage.' + getConfirmationStatusName(item.confirmationStatus)) }}
                          </v-chip>
                        </template>
                        <template v-slot:[`item.incidentCode`]="{ item }">
                          <v-chip v-if="item.incidentId" small label :color="getIncidentStatusColor(item.incidentStatus)" text-color="white"
                            style="cursor:pointer" @click.stop="openIncidentDetail(item.incidentId)">
                            <v-icon left small>mdi-alert-octagram</v-icon>
                            {{ item.incidentCode || getIncidentStatusName(item.incidentStatus) }}
                            <span v-if="item.incidentName" class="ml-1">— {{ item.incidentName }}</span>
                          </v-chip>
                        </template>
                      </v-data-table>
                    </v-tab-item>
                  </v-tabs>
                </td>
              </template>
              <template v-slot:[`item.incidents`]="{ item }">
                <v-chip v-if="item.hasOpenIncidents" small label color="orange" text-color="white">
                  <v-icon left small>mdi-alert</v-icon>
                  {{ $t('InvoicePage.OpenIncidents') }}
                </v-chip>
                <v-chip v-else small label color="success" text-color="white">
                  {{ $t('InvoicePage.NoIncidents') }}
                </v-chip>
              </template>
              <template v-slot:[`item.options`]="{ item }">
                <v-btn class="btn-action" icon small depressed :title="$t('label.reuploadInvoice')"
                  @click.stop="openReuploadInvoiceDialog(item)">
                  <v-icon color="primary">mdi-cloud-upload</v-icon>
                </v-btn>
                <v-btn v-if="item.invoiceStatus === 1 || item.invoiceStatus === 20" icon small depressed :title="$t('label.deleteInvoice')"
                  @click.stop="openDeleteInvoiceDialog(item)">
                  <v-icon color="error">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="isUploadInvoiceOpen" persistent scrollable width="800">
      <v-form v-model="isFormValid" ref="form">
        <v-card>
          <v-card-title>
            <v-row>
              <v-col cols="12">
                {{ $t('label.uploadInvoice') }}
              </v-col>
              <v-col v-if="userInfo.isForeignVendor" cols="12" style="color:red;">
                {{ $t('label.uploadInvoiceNote1') }}
              </v-col>
              <v-col v-if="!userInfo.isForeignVendor" cols="12" style="color:red;">
                {{ $t('label.uploadInvoiceNote') }}
              </v-col>
              <v-col cols="12">
                <v-file-input v-model="files" accept="application/pdf,application/xml" chips truncate-length="45"
                  :multiple="!userInfo.isForeignVendor" :disabled="isLoading" :label="$t('label.selectInvoiceShipment')"
                  :rules="[rules.atLeastOne]" :hint="$t('label.required')" :loading="loading.analyzeDocument"
                  persistent-hint @change="onFileChanged">
                </v-file-input>
              </v-col>
              <v-col cols="6" v-if="userInfo.isForeignVendor">
                <v-text-field v-model="invoiceCode" prepend-icon="mdi-file-document" :hint="$t('label.required')"
                  persistent-hint :label="$t('label.invoiceDocumentReq')"
                  :rules="userInfo.isForeignVendor === true ? [rules.required] : []">
                </v-text-field>
              </v-col>
              <v-col cols="6" v-if="userInfo.isForeignVendor">
                <v-text-field v-model="invoiceAmount" prepend-icon="mdi-cash" :disabled="isLoading"
                  :label="$t('label.invoiceAmount')"
                  :rules="userInfo.isForeignVendor === true ? [rules.required, rules.number] : []"
                  :hint="$t('label.required')" persistent-hint>
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="search" prepend-icon="mdi-magnify" :disabled="isLoading" hide-details
                  :label="$t('label.searchShipOrInvoice')">
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-data-table v-model="selectedShipments" :items="shipments" :loading="loading.shipments"
              :headers="unbilledShipmentHeaders" show-select disable-pagination hide-default-footer :search="search">
              <template v-slot:[`item.incidentCode`]="{ item }">
                <a :href="downloadIncidentUrl + item.incidentId" style="text-decoration: none">
                  <v-chip v-if="item.incidentStatus" :color="getIncidentStatusColor(item.incidentStatus)" label>
                    {{ getIncidentStatusName(item.incidentStatus) }}
                    <v-icon right style="cursor: pointer">
                      mdi-cloud-download
                    </v-icon>
                  </v-chip>
                </a>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer>
            </v-spacer>
            <v-btn small depressed @click.stop="isUploadInvoiceOpen = false" :disabled="isLoading">
              {{ $t('label.close') }}
            </v-btn>
            <v-btn small depressed color="primary" @click.stop="uploadInvoice"
              :disabled="!isFormValid || selectedShipments.length === 0 || loading.invoiceFiles || loading.analyzeDocument">
              {{ $t('label.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <v-dialog v-model="isReuploadInvoiceOpen" persistent scrollable width="800">
      <v-card>
        <v-card-title>
          {{ $t('label.currentInvoice') }}
          <v-spacer></v-spacer>
          <form ref="inputFileForm">
            <input type="file" ref="inputFile" multiple accept="application/pdf,application/xml" style="display:none"
              @change="previewFiles">
          </form>
        </v-card-title>
        <v-card-text>
          <v-data-table :loading="loading.invoiceFiles" :items="currentInvoices" :headers="invoiceHeaders"
            hide-default-footer>
            <template v-slot:[`item.name`]="{ item }">
              <v-row>
                <v-icon v-if="item.fileExtension === 1" class="mr-2" color="red">
                  mdi-file-pdf-box
                </v-icon>
                <v-icon v-if="item.fileExtension === 2" class="mr-2" color="blue">
                  mdi-xml
                </v-icon>
                <span>{{ item.name }}</span>
              </v-row>
            </template>
            <template v-slot:[`item.options`]="{ item }">
              <a :href="downloadUrl + item.id" style="text-decoration:none">
                <v-btn class="btn-action" icon :title="$t('label.download')" small depressed>
                  <v-icon color="primary">mdi-cloud-download</v-icon>
                </v-btn>
              </a>
            </template>
          </v-data-table>
          <br>
          <v-expansion-panels v-model="isHistoricExpanded">
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('label.historicInvoices') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table :loading="loading.invoiceFiles" :items="history" :headers="invoiceHistoryHeaders">
                  <template v-slot:[`item.name`]="{ item }">
                    <v-row>
                      <v-icon v-if="item.fileExtension === 1" class="mr-2" color="red">
                        mdi-file-pdf-box
                      </v-icon>
                      <v-icon v-if="item.fileExtension === 2" class="mr-2" color="blue">
                        mdi-xml
                      </v-icon>
                      <span>{{ item.name }}</span>
                    </v-row>
                  </template>
                  <template v-slot:[`item.options`]="{ item }">
                    <a :href="downloadUrl + item.id" style="text-decoration:none">
                      <v-btn class="btn-action" icon :title="$t('label.download')" small depressed>
                        <v-icon color="primary">mdi-cloud-download</v-icon>
                      </v-btn>
                    </a>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small depressed @click.stop="isReuploadInvoiceOpen = false">
            {{ $t('label.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isReplaceFilesConfirmationDialogOpen" persistent width="500">
      <confirmation-dialog @confirm="confirmReplace">{{ $t('message.confirmation.filesAlreadyExist', [fileNamesFound.join(', ')]) }}</confirmation-dialog>
    </v-dialog>
    <v-dialog v-model="isDetailDialogOpen" persistent width="1200">
      <shipment-detail :shipmentId="selectedShipmentId" @close="isDetailDialogOpen = false" :print="true"></shipment-detail>
    </v-dialog>
    <v-dialog v-model="isDeleteInvoiceDialogOpen" persistent width="500">
      <confirmation-dialog @confirm="confirmDelete">{{ $t('message.confirmation.deleteInvoice') }}</confirmation-dialog>
    </v-dialog>
    <v-dialog v-model="isReasonDialogOpen" persistent width="800">
      <v-card>
        <v-card-title>
          {{ $t('label.rejectReason') }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="selectedReason"
            outlined
            hide-details
            :label="$t('label.reason')"
            readonly
            >
          </v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer>
          </v-spacer>
          <v-btn small depressed @click.stop="isReasonDialogOpen = false">
            {{ $t('label.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isRejectInvoiceDialogOpen" persistent width="800">s
      <v-card>
        <v-card-title>
          {{ $t('InvoicePage.RejectInvoice') }}
        </v-card-title>
        <v-form>
          <v-card-text>
            <v-textarea v-model="rejectReason" outlined :label="$t('InvoicePage.Reason')" readonly>
            </v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer>
            </v-spacer>
            <v-btn small depressed @click.stop="isRejectInvoiceDialogOpen = false">
              {{ $t('InvoicePage.Close') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isHoldInvoiceDialogOpen" persistent width="800">
      <v-card>
        <v-card-title>
          {{ $t('InvoicePage.HoldInvoice') }}
        </v-card-title>
        <v-form>
          <v-card-text>
            <v-textarea v-model="selectedHoldReason" outlined :label="$t('InvoicePage.Reason')" readonly>
            </v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer>
            </v-spacer>
            <v-btn small depressed @click.stop="isHoldInvoiceDialogOpen = false">
              {{ $t('InvoicePage.Close') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isIncidentDetailOpen" max-width="720" scrollable>
      <incident-detail-card :incidentId="selectedIncidentId" @close="isIncidentDetailOpen = false"/>
    </v-dialog>
  </v-container>
</template>
<script>
import { loading, showMessage, rules, userInfo } from '../../mixins'
import ConfirmationDialog from '../../components/Shared/ConfirmationDialog.vue'
import IncidentDetailCard from '../../components/EDI/incidents/IncidentDetailCard.vue'
import ShipmentDetail from '../../components/EDI/shipments/ShipmentDetail.vue'
export default {
  mixins: [loading, showMessage, rules, userInfo],
  components: { ConfirmationDialog, IncidentDetailCard, ShipmentDetail },
  data: () => ({
    isDeleteInvoiceDialogOpen: false,
    isDetailDialogOpen: false,
    selectedShipmentId: null,
    downloadUrl: `${process.env.VUE_APP_API_URL}/api/shipments/download-invoice/`,
    search: '',
    invoices: [],
    shipments: [],
    selectedShipments: [],
    totalOfItems: 0,
    options: {},
    footerProps: {
      'items-per-page-options': [5, 10, 15, 30, 100]
    },
    currentInvoices: [],
    history: [],
    files: [],
    invoiceCode: '',
    invoiceAmount: '',
    vendors: [],
    loading: {
      invoices: false,
      shipments: false,
      billedShipments: false,
      invoiceFiles: false,
      vendors: false,
      analyzeDocument: false
    },
    filters: {
      shipmentId: '',
      invoice: '',
      vendor: null,
      invoiceStatus: null,
      isComplete: null,
      passDue: null,
      onHold: null
    },
    invoiceStatusList: [
      { name: 'Validation Pending', value: 1 },
      { name: 'Pending To Pay', value: 2 },
      { name: 'Partial Payment', value: 3 },
      { name: 'Payment Complete', value: 4 },
      { name: 'On Hold', value: 10 },
      { name: 'Rejected', value: 20 }
    ],
    isUploadInvoiceOpen: false,
    isReuploadInvoiceOpen: false,
    isReplaceFilesConfirmationDialogOpen: false,
    isRejectInvoiceDialogOpen: false,
    isHoldInvoiceDialogOpen: false,
    ShowHoldButton: false,
    isReasonDialogOpen: false,
    isConfirmationAcceptDialogOpen: false,
    isIncidentDetailOpen: false,
    selectedIncidentId: null,
    isFormValid: null,
    isFormSearchValid: null,
    expanded: [],
    selectedGroup: null,
    selectedFiles: [],
    fileNamesFound: [],
    isHistoricExpanded: false,
    selectedInvoice: null,
    rejectReason: null,
    holdReason: null,
    selectedHoldReason: null,
    selectedReason: '',
    currentItem: {}
  }),
  methods: {
    openDeleteInvoiceDialog (item) {
      this.selectedGroup = Object.assign({}, item)
      this.isDeleteInvoiceDialogOpen = true
    },
    async confirmDelete (confirm) {
      if (confirm) {
        await this.deleteInvoice()
      }
      this.isDeleteInvoiceDialogOpen = false
    },
    async deleteInvoice () {
      try {
        this.loading.invoices = true
        await this.$proxy.delete(`api/shipment-invoices/${this.selectedGroup.shipmentInvoiceId}`)
        await this.getInvoices()
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoices = false
      }
    },
    onFileChanged () {
      const file = Array.isArray(this.files) ? this.files[0] : this.files
      if (!file || !this.userInfo.isForeignVendor) {
        return
      }
      this.analyzeDocument(file)
    },
    async analyzeDocument (file) {
      try {
        this.loading.analyzeDocument = true
        const formData = new FormData()
        formData.append('file', file)
        const response = await this.$proxy.post('api/invoices/analyze', formData)
        this.invoiceCode = response.data.invoiceId
        this.invoiceAmount = response.data.invoiceTotal
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.analyzeDocument = false
      }
    },
    canReuploadInvoice (statusId) {
      if (statusId === 1 || statusId === 20 || statusId === 0) {
        return true
      }
      return false
    },
    async openUploadDialog () {
      this.files = []
      this.selectedShipments = []
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      await this.getUnbilledShipments()
      this.isUploadInvoiceOpen = true
    },
    async getInvoicesFiles () {
      try {
        this.loading.invoiceFiles = true
        const response = await this.$proxy.get(`api/shipments/${this.selectedGroup.shipmentInvoiceId}/invoice`)
        this.currentInvoices = response.data.current
        this.history = response.data.history
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoiceFiles = false
      }
    },
    openReuploadInvoiceDialog (item) {
      this.isReuploadInvoiceOpen = true
      this.selectedGroup = item
      this.getInvoicesFiles()
    },
    symmetricDifference (a1, a2) {
      var result = []
      for (var i = 0; i < a1.length; i++) {
        if (a2.indexOf(a1[i]) === -1) {
          result.push(a1[i])
        }
      }
      for (i = 0; i < a2.length; i++) {
        if (a1.indexOf(a2[i]) === -1) {
          result.push(a2[i])
        }
      }
      return result
    },
    difference (a1, a2) {
      var result = []
      var exist = false
      for (var i = 0; i < a1.length; i++) {
        exist = false
        for (var j = 0; j < a2.length; j++) {
          if (a1[i].IdCount === a2[j].IdCount) {
            exist = true
          }
        }
        if (!exist) {
          result.push(a1[i])
        }
      }
      return result
    },
    formatCurrency (value) {
      value = value.replace('$', '')
      return '$' + (value / 1).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    openAcceptConfirmationDialog (item) {
      this.selectedInvoice = Object.assign({}, item)
      this.isConfirmationAcceptDialogOpen = true
    },
    openReasonDialog (item) {
      this.selectedReason = item.rejectionReason
      this.isReasonDialogOpen = true
    },
    openHoldReasonDialog (item) {
      this.ShowHoldButton = false
      this.selectedHoldReason = item.holdReason
      this.isHoldInvoiceDialogOpen = true
    },
    openRejectDialog (item) {
      this.selectedInvoice = Object.assign({}, item)
      this.isRejectInvoiceDialogOpen = true
    },
    openHoldDialog (item) {
      this.ShowHoldButton = true
      this.selectedInvoice = Object.assign({}, item)
      this.isHoldInvoiceDialogOpen = true
    },
    confirmAcceptInvoice (confirm) {
      this.isConfirmationAcceptDialogOpen = false
      if (confirm) {
        this.acceptInvoice()
      }
    },
    async acceptInvoice () {
      try {
        this.loading.invoices = true
        await this.$proxy.put(`api/edi/invoices/accept/${this.selectedInvoice.shipmentInvoiceId}`)
        await this.getInvoices()
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoices = false
      }
    },
    async rejectInvoice () {
      try {
        this.loading.invoices = true
        const model = { reason: this.rejectReason }
        await this.$proxy.put(`api/edi/invoices/reject/${this.selectedInvoice.shipmentInvoiceId}`, model)
        await this.getInvoices()
        this.isRejectInvoiceDialogOpen = false
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoices = false
      }
    },
    async holdInvoice () {
      try {
        this.loading.invoices = true
        const model = { reason: this.holdReason }
        await this.$proxy.put(`api/edi/invoices/hold/${this.selectedInvoice.shipmentInvoiceId}`, model)
        await this.getInvoices()
        this.isHoldInvoiceDialogOpen = false
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoices = false
      }
    },
    async getInvoices () {
      try {
        this.loading.invoices = true
        this.expanded = []
        const params = new URLSearchParams()
        if (this.filters.invoice) {
          params.append('invoice', this.filters.invoice)
        }
        if (this.filters.shipmentId) {
          params.append('shipmentId', this.filters.shipmentId)
        }
        if (this.filters.dateFrom) {
          params.append('dateFrom', this.filters.dateFrom)
        }
        if (this.filters.invoiceStatus) {
          params.append('invoiceStatus', this.filters.invoiceStatus.value)
        }
        if (this.filters.isComplete !== null && this.filters.isComplete !== undefined) {
          params.append('isComplete', this.filters.isComplete)
        }
        if (this.filters.passDue !== null && this.filters.passDue !== undefined) {
          params.append('passDue', this.filters.passDue)
        }
        if (this.filters.onHold !== null && this.filters.onHold !== undefined) {
          params.append('onHold', this.filters.onHold)
        }

        params.append('vendorId', this.userInfo.vendorId)

        const { page, itemsPerPage } = this.options
        const response = await this.$proxy.get(`api/edi/invoices/${page}/${itemsPerPage}?${params.toString()}`)
        const invoices = response.data.items
        this.totalOfItems = response.data.totalOfItems
        invoices.forEach(i => {
          i.billedShipments = []
          i.summary = []
        })
        this.invoices = invoices
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoices = false
      }
    },
    async uploadInvoice () {
      try {
        this.loading.invoiceFiles = true
        const formData = new FormData()
        if (this.userInfo.isForeignVendor) {
          formData.append('files', this.files)
          formData.append('isForeignVendor', true)
        } else {
          this.files.forEach(file => formData.append('files', file))
          formData.append('isForeignVendor', false)
        }
        this.selectedShipments.forEach(s => formData.append('shipmentIds', s.id))
        if (this.invoiceCode) {
          formData.append('invoiceCode', this.invoiceCode)
        }
        if (this.invoiceAmount) {
          formData.append('invoiceAmount', this.invoiceAmount)
        }
        await this.$proxy.post('api/shipments/invoice', formData)
        await this.getInvoices()
        this.showMessage('Saved correctly')
        this.isUploadInvoiceOpen = false
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.invoiceFiles = false
      }
    },
    onExpanded (e) {
      const { item, value } = e
      this.currentItem = item
      if (value && item.billedShipments.length === 0) {
        this.getBilledShipments(item)
      }
    },
    async getBilledShipments (item) {
      try {
        this.loading.billedShipments = true
        const response = await this.$proxy.get(`api/edi/invoices/billed-shipments/${item.shipmentInvoiceId}`)
        this.$set(item, 'billedShipments', response.data.shipments)
        this.$set(item, 'summary', response.data.summary)
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.billedShipments = false
      }
    },
    async previewFiles () {
      const files = this.$refs.inputFile.files
      if (files.length === 0) {
        return
      }

      try {
        this.loading.invoiceFiles = true
        const fileNames = []
        this.selectedFiles = []
        const formData = new FormData()
        files.forEach(file => {
          fileNames.push(file.name)
          this.selectedFiles.push(file)
          formData.append('files', file)
        })
        const response = await this.$proxy.post(`api/shipments/verify-files/${this.selectedGroup.shipmentInvoiceId}`, fileNames)
        this.fileNamesFound = response.data
        if (this.fileNamesFound.length === 0) {
          await this.$proxy.post(`api/shipments/${this.selectedGroup.shipmentInvoiceId}/invoice-replace`, formData)
          this.showMessage('Upload correctly')
          this.getInvoices()
          this.getInvoicesFiles()
        } else {
          this.isReplaceFilesConfirmationDialogOpen = true
        }
      } catch (e) {
        this.showHttpErrorMessage(e)
      } finally {
        this.loading.invoiceFiles = false
        this.$refs.inputFileForm.reset()
      }
    },
    async confirmReplace (confirm) {
      if (confirm) {
        await this.uploadAndReplace()
      } else {
        this.$refs.inputFileForm.reset()
      }
      this.isReplaceFilesConfirmationDialogOpen = false
    },
    openInputFile () {
      this.$refs.inputFile.click()
    },
    async getUnbilledShipments () {
      try {
        this.loading.shipments = true
        const response = await this.$proxy.get('api/shipments/unbilled')
        this.shipments = response.data
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.shipments = false
      }
    },
    getShipmentStatusColor (statusId) {
      switch (statusId) {
        case 1:
          return 'gray'
        case 2:
          return 'yellow'
        case 3:
          return '#66BB6A'
      }
    },
    getMatchColor (status) {
      switch (status) {
        case true:
          return '#66BB6A'
        case false:
          return 'red'
      }
    },
    getPassDueColor (status) {
      switch (status) {
        case true:
          return '#66BB6A'
        case false:
          return 'red'
      }
    },
    getInvoiceStatusColor (statusId) {
      switch (statusId) {
        case 1:
          return 'gray'
        case 2:
          return 'blue'
        case 3:
          return 'yellow'
        case 4:
          return '#66BB6A'
        case 10:
          return 'gray'
        case 20:
          return '#FF5252'
      }
    },
    getInvoiceStatusTextColor1 (statusId) {
      switch (statusId) {
        case 1:
          return '#222'
        case 2:
          return '#FFF'
        case 3:
          return '#222'
        case 4:
          return '#FFF'
        case 10:
          return '#222'
        case 20:
          return '#FFF'
      }
    },
    getInvoiceStatusTextColor (statusId) {
      switch (statusId) {
        case 1:
          return '#222'
        case 2:
          return '#222'
        case 3:
          return '#FFF'
      }
    },
    getConfirmationStatusColor (statusId) {
      switch (statusId) {
        case 2:
          return '#FFB300'
        case 3:
          return '#66BB6A'
        default:
          return '#FB8C00'
      }
    },
    getConfirmationStatusTextColor (statusId) {
      return '#FFF'
    },
    getIncidentStatusColor (statusId) {
      switch (statusId) {
        case 1: return 'orange'
        case 2: return '#66BB6A'
        case 3: return 'grey'
        default: return 'grey'
      }
    },
    getIncidentStatusName (statusId) {
      switch (statusId) {
        case 1: return 'Open'
        case 2: return 'Resolved'
        case 3: return 'Cancelled'
        default: return ''
      }
    },
    async openIncidentDetail (incidentId) {
      this.selectedIncidentId = incidentId
      this.isIncidentDetailOpen = true
    },
    getStatusColor (statusId) {
      switch (statusId) {
        case 0:
          return '#FF5252'
        case 1:
          return '#66BB6A'
      }
    },
    getStatusTextColor (statusId) {
      switch (statusId) {
        case 0:
          return '#FFF'
        case 1:
          return '#FFF'
      }
    },
    SaveShipmentInvoceSelectedNode (event, item, shipmentInvoiceId) {
      this.loading.shipments = true
      const data = {
        IdFile: event.IdFile,
        Id: event.IdCount,
        PO: item.purchaseOrder,
        Line: item.line,
        Quantity: item.quantity
      }
      this.$proxy.post('api/edi/invoices/SaveShipmentInvoceSelectedNode', data)
        .then((r) => {
          this.getInvocieStatus(shipmentInvoiceId)
          this.getBilledShipments(this.currentItem)
          this.showMessage('Save correctly')
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loading.shipments = false
        })
    },
    async getInvocieStatus (shipmentInvoiceId) {
      try {
        const params = new URLSearchParams()
        if (this.filters.invoice) {
          params.append('invoice', this.filters.invoice)
        }
        if (this.filters.shipmentId) {
          params.append('shipmentId', this.filters.shipmentId)
        }
        if (this.filters.dateFrom) {
          params.append('dateFrom', this.filters.dateFrom)
        }
        if (this.filters.invoiceStatus) {
          params.append('invoiceStatus', this.filters.invoiceStatus.value)
        }
        if (this.filters.isComplete !== null && this.filters.isComplete !== undefined) {
          params.append('isComplete', this.filters.isComplete)
        }
        if (this.filters.passDue !== null && this.filters.passDue !== undefined) {
          params.append('passDue', this.filters.passDue)
        }
        if (this.filters.onHold !== null && this.filters.onHold !== undefined) {
          params.append('onHold', this.filters.onHold)
        }

        params.append('vendorId', this.userInfo.vendorId)

        const { page, itemsPerPage } = this.options
        const response = await this.$proxy.get(`api/edi/invoices/${page}/${itemsPerPage}?${params.toString()}`)
        this.invoices.filter(r => r.shipmentInvoiceId === shipmentInvoiceId)[0].isAcceptable = response.data.items.filter(r => r.shipmentInvoiceId === shipmentInvoiceId)[0].isAcceptable
      } catch (error) {
        this.showHttpErrorMessage(error)
      }
    },
    DeleteShipmentInvoceSelectedNode (event, item, shipmentInvoiceId) {
      this.loading.shipments = true
      const data = {
        IdFile: event.IdFile,
        Id: event.IdCount,
        PO: item.purchaseOrder,
        Line: item.line,
        Quantity: item.quantity
      }
      this.$proxy.post('api/edi/invoices/DeleteShipmentInvoceSelectedNode', data)
        .then((r) => {
          this.getInvocieStatus(shipmentInvoiceId)
          this.getBilledShipments(this.currentItem)
          this.showMessage('Save correctly')
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loading.shipments = false
        })
    },
    getQuantityStatusName (statusId) {
      switch (statusId) {
        case 0:
          return 'QuantityNotMatching'
        case 1:
          return 'QuantitiesMatching'
      }
    },
    getTotalStatusName (statusId) {
      switch (statusId) {
        case 0:
          return 'AmountNotMatching'
        case 1:
          return 'AmountMatching'
      }
    },
    getConfirmationStatusName (statusId) {
      switch (statusId) {
        case 2:
          return 'PartialReception'
        case 3:
          return 'ReceptionComplete'
        default:
          return 'PendingToReceive'
      }
    },
    getShipmentStatusName (statusId) {
      switch (statusId) {
        case 1:
          return 'PendingToReceive'
        case 2:
          return 'PartialReception'
        case 3:
          return 'MaterialReception'
      }
    },
    getInvoiceStatusName (statusId) {
      switch (statusId) {
        case 1:
          return 'ValidationPending'
        case 2:
          return 'PendingToPay'
        case 3:
          return 'PartialPayment'
        case 4:
          return 'PaymentComplete'
        case 10:
          return 'OnHold'
        case 20:
          return 'Rejected'
      }
    }
  },
  computed: {
    isRejectDisabled () {
      if (this.rejectReason) {
        return false
      } else {
        return true
      }
    },
    isHoldDisabled () {
      if (this.holdReason) {
        return false
      } else {
        return true
      }
    },
    threeWayList () {
      return [
        { text: this.$t('InvoicePage.3WayComplete'), value: true },
        { text: this.$t('InvoicePage.3WayErrors'), value: false }
      ]
    },
    passDueList () {
      return [
        { text: this.$t('InvoicePage.PassDue'), value: true },
        { text: this.$t('InvoicePage.NotPassDue'), value: false }
      ]
    },
    onHoldList () {
      return [
        { text: this.$t('InvoicePage.OnHold'), value: true },
        { text: this.$t('InvoicePage.NotOnHold'), value: false }
      ]
    },
    headers () {
      const headers = [
        { text: this.$t('InvoicePage.InvoiceId'), value: 'shipmentInvoiceId', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.Vendor'), value: 'vendorDescription', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.UploadDate'), value: 'formattedUploadDate', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.DueDate'), value: 'dueDateFormatted', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.InvoiceStatus'), value: 'invoiceStatus', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.3Way'), value: 'shipmentStatus', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.Incidents'), value: 'incidents', align: 'center', sortable: false, filterable: false },
        { text: 'Options', value: 'options', align: 'center', sortable: false, filterable: false }
      ]
      return headers
    },
    headersExpand () {
      const headers = [
        { text: this.$t('InvoicePage.ShipId'), value: 'shipmentId', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.CreationDate'), value: 'creationDateFormatted', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.ShipmentAmount'), value: 'totalShipmentFormatted', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.ConfirmationStatus'), value: 'confirmationStatus', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.SuplierReferenceCode'), value: 'invoice', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.Incident'), value: 'incidentCode', align: 'center', sortable: false, filterable: false }
      ]
      return headers
    },
    Summary () {
      const headers = [
        { text: this.$t('InvoicePage.ShippedQuantity'), value: 'quantityToShip', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.GoodRecipt'), value: 'confirmedQuantity', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.InvoiceQuantity'), value: 'quantity2', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.POAmount'), value: 'poAmountFormatted', align: 'center', sortable: false, filterable: false },
        { text: this.$t('InvoicePage.InvoiceAmount'), value: 'invoiceAmountFormatted', align: 'center', sortable: false, filterable: false }
      ]
      return headers
    },
    invoiceHeaders () {
      return [
        { text: 'File name', value: 'name', align: 'center', sortable: false, filterable: true },
        { text: 'Upload date', value: 'uploadDateFormated', align: 'center', sortable: false, filterable: true },
        { text: 'Options', value: 'options', align: 'center', sortable: false, filterable: true }
      ]
    },
    invoiceHistoryHeaders () {
      return [
        { text: 'File name', value: 'name', align: 'center', sortable: false, filterable: true },
        { text: 'Upload date', value: 'uploadDateFormated', align: 'center', sortable: false, filterable: true },
        { text: 'Options', value: 'options', align: 'center', sortable: false, filterable: false }
      ]
    },
    unbilledShipmentHeaders () {
      const headers = [
        { text: '', value: 'data-table-select', class: 'sticky-header white' },
        { text: 'Ship Id', value: 'id', align: 'center', sortable: false, filterable: true, class: 'sticky-header white' },
        { text: 'Creation date', value: 'creationDateFormatted', align: 'center', sortable: false, filterable: true, class: 'sticky-header white' },
        { text: 'Suplier Reference Code', value: 'invoice', align: 'center', sortable: false, filterable: true, class: 'sticky-header white' }
      ]
      return headers
    }
  },
  watch: {
    options: {
      handler (newVal, oldVal) {
        if (!oldVal.page) {
          return
        }
        this.getInvoices()
      },
      immediate: false
    },
    isUploadInvoiceOpen: {
      handler (val) {
        if (val) {

        }
      }
    }
  }
}
</script>
